import {useIsClient, useResizeObserver} from 'usehooks-ts'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import {useRef} from 'react'
import clsx from 'clsx'
import {internalGroqTypeReferenceTo, SanityImageCrop, SanityImageHotspot} from 'sanity.types'
import CmsImage from '~/components/content/CmsImage'
import Carousel from '~/components/common/Carousel'

type Slider = {
    _key: string
    _type: 'slider'
    slides: Array<{
        image: {
            asset?: {
                _ref: string
                _type: 'reference'
                _weak?: boolean
                [internalGroqTypeReferenceTo]?: 'sanity.imageAsset'
            }
            hotspot?: SanityImageHotspot
            crop?: SanityImageCrop
            alt: string
            backgroundColor?: string
            _type: 'image'
            metadata: {
                lqip: string | null
            }
        }
        content: {
            position: 'center' | 'left-bottom'
            title?: string
            description: string
            link?: {
                label: string
                link: string
            }
        }
        _type: 'slide'
        _key: string
    }> | null
}

type SliderProps = Slider

const config: BlazeConfig = {
    all: {
        slidesToShow: 1,
        slideGap: '0px',
        loop: true,
        // enableAutoplay: true,
        autoplayInterval: 8000,
        stopAutoplayOnInteraction: true,
    },
}

const Slider = ({slides}: SliderProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const {width = 0, height = 0} = useResizeObserver({ref})
    const isClient = useIsClient()

    if (!slides?.length) {
        return null
    }

    return (
        <div
            ref={ref}
            className="mx-auto relative backdrop-blur transform-gpu bg-gray-700/10 max-w-[1920px]"
        >
            <Carousel config={config} showDots={false} showDisabledArrows={false}>
                {slides.map((slide, index) => (
                    <div
                        key={slide._key}
                        className="relative h-full min-h-[80svh] lg:min-h-[800px]"
                    >
                        {!!slide.image && (
                            <CmsImage
                                className="absolute size-full shadow-2xl object-cover object-center"
                                image={slide.image}
                                backgroundColor={slide.image.backgroundColor}
                                mode="cover"
                                expectedSize={
                                    isClient
                                        ? {
                                              width: Math.min(width, 1920),
                                              height: Math.min(height, 800),
                                          }
                                        : undefined
                                }
                                loading={index === 0 ? 'eager' : 'lazy'}
                                fetchPriority={index === 0 ? 'high' : undefined}
                                quality={100}
                            />
                        )}
                        <div className="absolute size-full inset-0">
                            <div
                                className={clsx(
                                    ' h-full flex flex-col',
                                    slide.content.position === 'center'
                                        ? 'items-center justify-center'
                                        : 'items-start justify-end'
                                )}
                            >
                                <div
                                    className={clsx(
                                        'backdrop-blur transform-gpu bg-[#16161daf] w-full mx-auto xl:rounded-t-xl',
                                        slide.content.position === 'center'
                                            ? 'xl:rounded-b-xl max-w-screen-md'
                                            : 'max-w-screen-xl'
                                    )}
                                >
                                    <div
                                        className={clsx(
                                            ' space-y-6 py-8 px-10 md:px-12 xl:px-16',
                                            slide.content.position === 'center' ? 'text-center' : ''
                                        )}
                                    >
                                        {!!slide.content.title && (
                                            <h2 className="text-3xl md:text-5xl font-bold">
                                                {slide.content.title}
                                            </h2>
                                        )}
                                        <p className="text-lg leading-6 md:text-2xl">
                                            {slide.content.description}
                                        </p>
                                        {!!slide.content.link && (
                                            <div>
                                                <Link
                                                    to={slide.content.link.link}
                                                    className=" inline-block text-white px-4 py-3  font-medium rounded-lg border-2 shadow-sm text-xl md:text-2xl"
                                                >
                                                    {slide.content.link.label}
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </Carousel>
        </div>
    )
}

export default Slider
